import React from 'react'
import { Drawer, Select, Empty, Modal } from 'antd'
import { CheckCircle2, Truck, Info, Clock, ArrowRight } from 'lucide-react'
import NumberFormat from 'react-number-format'
import { LocationWrapper } from './LocationWrapper'
import FooterDisplay from './footer'
import ImageSlider from './sliders/ImageSlider'
import RestaurantCard from './Restaurant/RestaurantCard'
import { useBusinessListData } from '../hooks/useBusinessListData'
import useMinimumOrder from '../hooks/useMinimumOrder'
import ValentineImg from '../assets/images/restaurant/vALENTInes.png'
import HelpSupportButton from './HelpSupportButton'

const BusinessListingPage = ({
  title,
  bannerText,
  businessType,
  promoImages
}) => {
  const { minimumOrder } = useMinimumOrder(businessType || "restaurant")
  const {
    loading,
    error,
    selectedLGA,
    selectedState,
    isModalVisible,
    setIsModalVisible,
    businessData: businesses,
    all_lgas,
    allStates,
    loadingCities,
    handleLGAChange,
    handleStateSelect,
    searchQuery,
    handleSearchChange
  } = useBusinessListData({ businessType })

  const [promoModalVisible, setPromoModalVisible] = React.useState(false)
  const [selectedPromo, setSelectedPromo] = React.useState(null)

  const handleBannerClick = (promo) => {
    setSelectedPromo(promo)
    setPromoModalVisible(true)
  }

  const isRawFood = businessType === 'raw_food'

  return (
    <div className='pb-[80px]' style={{ backgroundColor: '#F2F2F2' }}>
      <LocationWrapper
        selectLoc={true}
        selectedLGA={selectedLGA?.name}
        selectedState={selectedState}
        handleStateSelect={handleStateSelect}
        showCart={isRawFood}
        handleLGAChange={handleLGAChange}
        businessType={businessType}
        lagosLga={all_lgas}
        allStates={allStates}
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
      />

      <div className='mt-8 md:mt-14 mb-8 md:mb-12'>
        <div className='relative w-full overflow-hidden rounded-xl md:rounded-3xl h-[100px]'>
          <img
            src={ValentineImg}
            alt='promo'
            className='w-full h-full object-cover object-top'
          />
          <div className='absolute inset-0 flex items-center justify-center -translate-y-1'>
            <div className='text-center text-white'>
              <div className='text-3xl md:text-6xl font-bold'>{bannerText}</div>
            </div>
          </div>
        </div>
      </div>

      <div className='mt-2 md:mt-16 px-4 md:px-24 mb-12'>
        <div className='grid grid-cols-1 gap-4 md:gap-10'>
          <div>
            <ImageSlider
              images={promoImages?.map(img => ({
                ...img,
                onBannerClick: handleBannerClick
              }))}
            />
          </div>
        </div>
      </div>

      <div className='mt-16 px-4 md:px-24 md:mb-10'>
        {title && <h4 className='text-4xl mb-4 md:mb-2 font-bold'>{title}</h4>}
        {loading && (
          <div className='text-center py-8'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
            <p className='mt-2 text-gray-600'>Loading {businessType}...</p>
          </div>
        )}
        {error && (
          <div className='text-center py-8'>
            <p className='text-red-500 text-xl'>
              Error loading {businessType}: {error}
            </p>
          </div>
        )}
        {!loading && !error && (
          <>
            {businesses && businesses.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:gap-10">
                {businesses?.map((business) => (
                  <RestaurantCard
                    key={business.id || business.businessId}
                    businessType={businessType}
                    restaurant={business}
                  />
                ))}
              </div>
            ) : (
              <Empty
                description={`No ${businessType} found in this area`}
              ></Empty>
            )}
          </>
        )}
      </div>

      <FooterDisplay />
      <HelpSupportButton page="SERVICE" />

      <Drawer
        title='Select Your Location'
        footer={null}
        onClose={() => setIsModalVisible(false)}
        open={isModalVisible}
        placement='bottom'
        className='partnerModal'
      >
        <div>
          <h2 className='text-gray-900 font-bold flex items-center my-4 mt-8 text-5xl'>
            Select Your Location
          </h2>
          <div className='mt-8'>
            <Select
              value={selectedLGA?.name}
              onChange={handleLGAChange}
              className='w-full rest_searchDrawer'
              placeholder='Select Location'
              disabled={all_lgas?.length === 0 || loadingCities}
            >
              {all_lgas?.length === 0 ? (
                <Select.Option value='' disabled>
                  Select Location
                </Select.Option>
              ) : (
                all_lgas?.map(lga => (
                  <Select.Option key={lga.id} value={lga.name}>
                    {lga.name}
                  </Select.Option>
                ))
              )}
            </Select>
          </div>
        </div>
      </Drawer>

      <Modal
        open={promoModalVisible}
        onCancel={() => setPromoModalVisible(false)}
        footer={null}
        closable={false}
        centered
        width={400}
        styles={{ body: { padding: '6px' } }}
      >
        {selectedPromo && (
          <div className="flex flex-col items-center text-center">
            <div className={`p-4 ${selectedPromo.bgColor} rounded-2xl mb-4`}>
              {selectedPromo.tag === 'FREE DELIVERY' ? (
                <Truck className={`w-8 h-8 ${selectedPromo.textColor}`} />
              ) : (
                <Info className={`w-8 h-8 ${selectedPromo.textColor}`} />
              )}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2 uppercase italic">
              {selectedPromo.title}
            </h2>

            <p className="text-gray-500 mb-6">
              {selectedPromo.subtitle}
            </p>

            <div className="w-full space-y-4 mb-8 text-left">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-bold  text-gray-900">Minimum Order</h4>
                  <p className=" text-gray-500">Valid for orders above ₦{minimumOrder ? Number(minimumOrder).toLocaleString() : '5,000'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <Truck className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-bold  text-gray-900">Distance Limit</h4>
                  <p className=" text-gray-500">Valid within 5km from the restaurant</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <Clock className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h4 className="font-bold  text-gray-900">Limited Time Only</h4>
                  <p className=" text-gray-500">Offer may change without prior notice</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setPromoModalVisible(false)}
              className={`w-full py-4 rounded-xl text-white font-bold shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 ${selectedPromo.buttonColor}`}
            >
              Start Ordering Now <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-4  text-gray-400">Terms and conditions apply</p>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default BusinessListingPage
